import { useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  Grid,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { Calendar, Pencil, Ruler, UserRound, Cake } from 'lucide-react';
import type { PatientBaseProfile } from '../types';
import {
  formatAgeValue,
  formatBiologicalSex,
  formatBirthYearValue,
  formatMeasurementValue,
} from '../utils/formatters';
import { deriveAgeFromDateOfBirth } from '../utils/nutrition';
import {
  birthYearToDateOfBirth,
  getMaxBirthYear,
  MIN_BIRTH_YEAR,
  parseBirthYear,
} from '../utils/profile';
import { PatientStatTile, type PatientStatTileProps } from './PatientStatTile';

interface PatientBaseProfileCardProps {
  userCode: string;
  profile: PatientBaseProfile;
  saving?: boolean;
  onSubmit: (values: PatientBaseProfile) => Promise<void> | void;
}

const toFormValues = (profile: PatientBaseProfile): PatientBaseProfile => ({
  biologicalSex: profile.biologicalSex,
  dateOfBirth: profile.dateOfBirth ?? '',
  heightCm: profile.heightCm,
});

type ProfileStat = PatientStatTileProps & {
  key: string;
};

export function PatientBaseProfileCard({
  userCode,
  profile,
  saving = false,
  onSubmit,
}: PatientBaseProfileCardProps) {
  const [editOpened, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);
  const formValues = useMemo(() => toFormValues(profile), [profile]);
  const form = useForm<PatientBaseProfile>({
    initialValues: formValues,
  });

  useEffect(() => {
    if (!editOpened) {
      form.setValues(formValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, editOpened]);

  const age = deriveAgeFromDateOfBirth(profile.dateOfBirth);

  const stats: ProfileStat[] = [
    {
      key: 'sex',
      label: 'Sesso biologico',
      value: formatBiologicalSex(profile.biologicalSex),
      icon: UserRound,
      color: 'blue',
    },
    {
      key: 'dob',
      label: 'Anno di nascita',
      value: formatBirthYearValue(profile.dateOfBirth),
      icon: Calendar,
      color: 'violet',
    },
    {
      key: 'age',
      label: 'Età stimata',
      value: formatAgeValue(age),
      icon: Cake,
      color: 'teal',
    },
    {
      key: 'height',
      label: 'Altezza',
      value: formatMeasurementValue(profile.heightCm, 'cm'),
      icon: Ruler,
      color: 'orange',
    },
  ];

  const handleOpenEdit = () => {
    form.setValues(formValues);
    openEdit();
  };

  const handleCloseEdit = () => {
    form.setValues(formValues);
    closeEdit();
  };

  const handleSubmit = form.onSubmit(async values => {
    await onSubmit(values);
    closeEdit();
  });

  return (
    <>
      <Card withBorder p='md'>
        <Stack gap='md'>
          <Group justify='space-between' align='center' wrap='wrap'>
            <Text fw={600} size='lg'>
              Codice utente: #{userCode.replace(/^#/, '')}
            </Text>
            <Button
              variant='light'
              leftSection={<Pencil size={16} />}
              onClick={handleOpenEdit}
            >
              Modifica
            </Button>
          </Group>

          <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing='md'>
            {stats.map(({ key, ...stat }) => (
              <PatientStatTile key={key} {...stat} />
            ))}
          </SimpleGrid>
        </Stack>
      </Card>

      <Modal
        opened={editOpened}
        onClose={handleCloseEdit}
        title='Modifica profilo base'
        centered
        size='lg'
      >
        <form onSubmit={handleSubmit}>
          <Stack gap='lg'>
            <Text size='sm' c='dimmed'>
              Aggiorna i dati anagrafici usati per metriche e contesto clinico.
            </Text>

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label='Sesso biologico'
                  placeholder='Seleziona'
                  data={[
                    { value: 'male', label: 'Maschio' },
                    { value: 'female', label: 'Femmina' },
                  ]}
                  clearable
                  disabled={saving}
                  {...form.getInputProps('biologicalSex')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label='Anno di nascita'
                  description='Solo anno: serve per stimare l’età'
                  placeholder='Es. 1991'
                  min={MIN_BIRTH_YEAR}
                  max={getMaxBirthYear()}
                  allowDecimal={false}
                  disabled={saving}
                  value={parseBirthYear(form.values.dateOfBirth) ?? ''}
                  error={form.errors.dateOfBirth}
                  onChange={value => {
                    const year =
                      typeof value === 'number'
                        ? value
                        : Number.parseInt(String(value), 10);
                    if (!Number.isInteger(year)) {
                      form.setFieldValue('dateOfBirth', '');
                      return;
                    }
                    form.setFieldValue(
                      'dateOfBirth',
                      birthYearToDateOfBirth(year)
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12 }}>
                <NumberInput
                  label='Altezza (cm)'
                  placeholder='Es. 172'
                  min={0}
                  decimalScale={2}
                  disabled={saving}
                  {...form.getInputProps('heightCm')}
                />
              </Grid.Col>
            </Grid>

            <Group justify='flex-end'>
              <Button
                variant='default'
                onClick={handleCloseEdit}
                disabled={saving}
              >
                Annulla
              </Button>
              <Button type='submit' loading={saving}>
                Salva modifiche
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
